import { Box, Button, Grid, GridItem, Heading, HStack, Stack, Text } from '@chakra-ui/react'
import type { AuctionWithHighestBid } from '@twlvxtwlv/supabase'
import { calculateAuctionState } from '@twlvxtwlv/supabase'
import { toLocalizedDateTime, toLocalizedEuro } from '@twlvxtwlv/utils/src/localization'
import { useTranslation } from 'next-i18next'
import NextLink from 'next/link'
import { useState } from 'react'
import { Countdown } from '../../../../../../shared/countdown'
import { PulsatingDot } from '../../../../../../shared/pulsating-dot'

interface AuctionCtaProps {
  auctionWithHighestBid: AuctionWithHighestBid
  color?: 'black' | 'white'
}

enum AuctionState {
  ready = 'ready',
  active = 'active',
  finished = 'finished',
}

const tenMinutesInMs = 600000

export function AuctionCta({ auctionWithHighestBid, color = 'black' }: AuctionCtaProps) {
  const { t } = useTranslation('auction')

  const headingColor = color === 'black' ? 'txtWhite' : 'txtWhite'
  const [auctionState, setAuctionState] = useState(
    calculateAuctionState(
      auctionWithHighestBid.startedAt ?? '',
      auctionWithHighestBid.finishedAt ?? ''
    )
  )

  const auctionStartDisplay = () => {
    if (auctionState) {
      switch (auctionState) {
        case AuctionState.ready:
          if (Date.now() >= Date.parse(auctionWithHighestBid.startedAt) - tenMinutesInMs) {
            return (
              <Box w="full" fontSize={['md', 'lg', 'lg']} fontWeight="600">
                <Countdown
                  date={auctionWithHighestBid.startedAt}
                  onComplete={() => setAuctionState(AuctionState.active)}
                />
              </Box>
            )
          }
          return (
            <Text fontWeight="600" fontSize={['md', 'lg', 'lg']} color={color}>
              {toLocalizedDateTime(auctionWithHighestBid.startedAt)}
            </Text>
          )
        case AuctionState.active:
          return (
            <Text fontWeight="600" fontSize={['md', 'lg', 'lg']} color={color}>
              {toLocalizedDateTime(auctionWithHighestBid.startedAt)}
            </Text>
          )
        case AuctionState.finished:
          return null
      }
    }
  }

  const auctionEndDisplay = () => {
    if (auctionState === AuctionState.active) {
      return (
        <HStack align="center" fontSize={['md', 'lg', 'lg']} fontWeight="600">
          <PulsatingDot />
          <Countdown
            date={auctionWithHighestBid.finishedAt}
            onComplete={() => setAuctionState(AuctionState.finished)}
          />
        </HStack>
      )
    } else {
      return (
        <Text w="full" fontWeight="600" fontSize={['md', 'lg', 'lg']}>
          {auctionState === AuctionState.ready
            ? toLocalizedDateTime(auctionWithHighestBid.finishedAt)
            : t('auction-finished')}
        </Text>
      )
    }
  }

  return (
    <Grid
      gap={4}
      w="full"
      templateAreas={[
        `"auctionStartedAt auctionStartedAt" "auctionFinishedAt highestBid" "button button"`,
        `"auctionStartedAt auctionStartedAt" "auctionFinishedAt highestBid" "button button"`,
        `"auctionStartedAt highestBid" "auctionFinishedAt button"`,
      ]}
      gridTemplateColumns={'1fr 1fr'}
      color={color}
    >
      <GridItem area="auctionStartedAt">
        <Stack spacing={1}>
          {auctionStartDisplay()}

          {auctionState !== AuctionState.finished && (
            <Heading fontSize={['xs', 'xs', 'sm']} fontWeight="400" color={headingColor}>
              {t('started-at-label')}
            </Heading>
          )}
        </Stack>
      </GridItem>

      <GridItem area="auctionFinishedAt">
        <Stack spacing={1}>
          {auctionEndDisplay()}
          <Heading fontSize={['xs', 'xs', 'sm']} fontWeight="400" color={headingColor}>
            {t('finished-at-label')}
          </Heading>
        </Stack>
      </GridItem>

      <GridItem area="highestBid">
        <Stack spacing={1} align="flex-end">
          <Text fontWeight="600" fontSize={['md', 'lg', 'lg']} color={color}>
            {toLocalizedEuro(auctionWithHighestBid?.highestBidInEuros * 100)}
          </Text>
          <Heading fontSize={['xs', 'xs', 'sm']} fontWeight="400" color={headingColor}>
            {auctionState === AuctionState.ready
              ? t('start-price')
              : auctionState === AuctionState.active
              ? t('current-price')
              : t('highest-bid-label')}
          </Heading>
        </Stack>
      </GridItem>

      <GridItem area="button" my={2}>
        <Button
          as={NextLink}
          variant="txtSignalLime"
          href={`/nft/${auctionWithHighestBid.tfProductId}`}
          passHref
        >
          {t('to-auction')}
        </Button>
      </GridItem>
    </Grid>
  )
}
